import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Upload,
  Play,
  Download,
  Trash2,
  File,
  FileSpreadsheet,
  LogOut,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Loader2,
  FolderInput,
  FolderOutput,
  Send,
  Mail,
  MessageCircle,
  StopCircle
} from 'lucide-react';
import { Toaster, toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatDate(isoString) {
  const date = new Date(isoString);
  return date.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('input');
  const [inputFiles, setInputFiles] = useState([]);
  const [outputFiles, setOutputFiles] = useState([]);
  const [processStatus, setProcessStatus] = useState({
    status: 'idle',
    progress: 0,
    message: '',
    current_step: null,
    total_rows: 0,
    processed_rows: 0
  });
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const editedFileInputRef = useRef(null);
  const statusIntervalRef = useRef(null);
  const sendIntervalRef = useRef(null);
  
  // Send outreach state
  const [sendStatus, setSendStatus] = useState({
    status: 'idle',
    progress: 0,
    message: '',
    total_contacts: 0,
    sent_emails: 0,
    sent_whatsapp: 0,
    sent_sms: 0,
    errors: 0
  });
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedFileForSend, setSelectedFileForSend] = useState(null);
  const [sendOptions, setSendOptions] = useState({
    send_email: true,
    send_whatsapp: true,
    dry_run: false
  });

  const fetchFiles = useCallback(async () => {
    try {
      const [inputRes, outputRes] = await Promise.all([
        axios.get(`${API_URL}/api/files/input`, { withCredentials: true }),
        axios.get(`${API_URL}/api/files/output`, { withCredentials: true })
      ]);
      setInputFiles(inputRes.data.files);
      setOutputFiles(outputRes.data.files);
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  }, []);

  const fetchProcessStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/process/status`, { withCredentials: true });
      setProcessStatus(response.data);
      
      if (response.data.status === 'completed') {
        toast.success('Elaborazione completata!');
        fetchFiles();
        clearInterval(statusIntervalRef.current);
      } else if (response.data.status === 'error') {
        toast.error(response.data.message);
        clearInterval(statusIntervalRef.current);
      }
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  }, [fetchFiles]);

  // Send status polling
  const fetchSendStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/send/status`, { withCredentials: true });
      setSendStatus(response.data);
      
      if (response.data.status === 'completed') {
        toast.success(`Invio completato! Email: ${response.data.sent_emails}, WhatsApp: ${response.data.sent_whatsapp}`);
        clearInterval(sendIntervalRef.current);
      } else if (response.data.status === 'stopped') {
        toast.warning(`Invio interrotto. Email: ${response.data.sent_emails}, WhatsApp: ${response.data.sent_whatsapp}`);
        clearInterval(sendIntervalRef.current);
      } else if (response.data.status === 'error') {
        toast.error(response.data.message);
        clearInterval(sendIntervalRef.current);
      }
    } catch (error) {
      console.error('Error fetching send status:', error);
    }
  }, []);

  // ── FIX: al caricamento della pagina controlla entrambi gli stati
  // e riprende il polling automaticamente se erano in corso ──────────────────
  useEffect(() => {
    fetchFiles();

    const initStatus = async () => {
      try {
        const [processRes, sendRes] = await Promise.all([
          axios.get(`${API_URL}/api/process/status`, { withCredentials: true }),
          axios.get(`${API_URL}/api/send/status`,   { withCredentials: true })
        ]);

        // Ripristina stato elaborazione
        setProcessStatus(processRes.data);
        if (processRes.data.status === 'running') {
          // Era in corso quando la pagina è stata ricaricata → riprendi polling
          statusIntervalRef.current = setInterval(fetchProcessStatus, 1000);
        }

        // Ripristina stato invio
        setSendStatus(sendRes.data);
        if (sendRes.data.status === 'running') {
          // Era in corso quando la pagina è stata ricaricata → riprendi polling
          sendIntervalRef.current = setInterval(fetchSendStatus, 1000);
        }
      } catch (error) {
        console.error('Error initializing status on mount:', error);
      }
    };

    initStatus();

    return () => {
      if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
      if (sendIntervalRef.current)   clearInterval(sendIntervalRef.current);
    };
  }, [fetchFiles, fetchProcessStatus, fetchSendStatus]);

  const openSendModal = (filename) => {
    setSelectedFileForSend(filename);
    setShowSendModal(true);
  };

  const startSending = async () => {
    if (!selectedFileForSend) return;
    
    try {
      await axios.post(`${API_URL}/api/send/start`, {
        filename: selectedFileForSend,
        ...sendOptions
      }, { withCredentials: true });
      
      toast.info(sendOptions.dry_run ? 'Test invio avviato (DRY RUN)' : 'Invio avviato');
      setShowSendModal(false);
      
      sendIntervalRef.current = setInterval(fetchSendStatus, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'avvio dell\'invio');
    }
  };

  const resetSendStatus = async () => {
    try {
      await axios.post(`${API_URL}/api/send/reset`, {}, { withCredentials: true });
      setSendStatus({
        status: 'idle',
        progress: 0,
        message: '',
        total_contacts: 0,
        sent_emails: 0,
        sent_whatsapp: 0,
        sent_sms: 0,
        errors: 0
      });
      if (sendIntervalRef.current) {
        clearInterval(sendIntervalRef.current);
      }
    } catch (error) {
      console.error('Error resetting send:', error);
    }
  };

  const stopSending = async () => {
    try {
      await axios.post(`${API_URL}/api/send/stop`, {}, { withCredentials: true });
      toast.warning('Stop invio richiesto...');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nello stop');
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    const allowedTypes = ['.xlsx', '.xls', '.csv'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(ext)) {
      toast.error('Solo file Excel (.xlsx, .xls) o CSV sono accettati');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API_URL}/api/files/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`File "${file.name}" caricato con successo`);
      fetchFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setUploading(false);
    }
  };

  // Carica un CSV già editato (es. file _check verificato a mano) direttamente
  // nella cartella output, lo rende inviabile e apre subito la finestra di invio.
  const handleEditedUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Per l\'invio carica un file CSV');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'output');

    try {
      const res = await axios.post(`${API_URL}/api/files/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`File "${res.data.filename}" pronto per l'invio`);
      await fetchFiles();
      setActiveTab('output');
      openSendModal(res.data.filename);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDownload = async (folder, filename) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/files/download/${folder}/${filename}`,
        { withCredentials: true, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore nel download del file');
    }
  };

  const handleDelete = async (folder, filename) => {
    if (!window.confirm(`Sei sicuro di voler eliminare "${filename}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/files/${folder}/${filename}`, {
        withCredentials: true
      });
      toast.success('File eliminato');
      fetchFiles();
    } catch (error) {
      toast.error('Errore nell\'eliminazione del file');
    }
  };

  const startProcessing = async () => {
    try {
      await axios.post(`${API_URL}/api/process/start`, {}, { withCredentials: true });
      toast.info('Elaborazione avviata');
      statusIntervalRef.current = setInterval(fetchProcessStatus, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'avvio dell\'elaborazione');
    }
  };

  const resetProcess = async () => {
    try {
      await axios.post(`${API_URL}/api/process/reset`, {}, { withCredentials: true });
      setProcessStatus({
        status: 'idle',
        progress: 0,
        message: '',
        current_step: null,
        total_rows: 0,
        processed_rows: 0
      });
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
      }
    } catch (error) {
      console.error('Error resetting process:', error);
    }
  };

  const getStatusBadgeClass = () => {
    switch (processStatus.status) {
      case 'running':   return 'status-badge status-running';
      case 'completed': return 'status-badge status-completed';
      case 'error':     return 'status-badge status-error';
      default:          return 'status-badge status-idle';
    }
  };

  const getStatusText = () => {
    switch (processStatus.status) {
      case 'running':   return 'In esecuzione';
      case 'completed': return 'Completato';
      case 'error':     return 'Errore';
      default:          return 'In attesa';
    }
  };

  const files = activeTab === 'input' ? inputFiles : outputFiles;

  return (
    <div className="dashboard-container" data-testid="dashboard">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Lead Engine
            </h1>
            <p className="text-sm text-gray-500">Sistema di Lead Generation</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">
              {user?.email}
            </span>
            <button
              onClick={logout}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline">Esci</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Upload & Process Section */}
          <div className="lg:col-span-1 space-y-6">
            {/* Upload Card */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Carica File
              </h2>
              
              <div
                className={`upload-zone ${dragOver ? 'drag-over' : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-zone"
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => handleFileUpload(e.target.files[0])}
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  data-testid="file-input"
                />
                
                {uploading ? (
                  <Loader2 className="w-12 h-12 text-[#002DB3] mx-auto mb-3 animate-spin" />
                ) : (
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                )}
                
                <p className="text-gray-600 font-medium">
                  {uploading ? 'Caricamento...' : 'Trascina qui il file'}
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  oppure clicca per selezionare
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  Formati: .xlsx, .xls, .csv
                </p>
              </div>
            </div>

            {/* Process Card */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                  Elaborazione
                </h2>
                <span className={getStatusBadgeClass()} data-testid="process-status-badge">
                  {getStatusText()}
                </span>
              </div>

              {/* Progress Bar */}
              {(processStatus.status === 'running' || processStatus.status === 'completed') && (
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>{processStatus.message}</span>
                    <span data-testid="progress-percentage">{processStatus.progress}%</span>
                  </div>
                  <div className="progress-bar-container" data-testid="progress-bar">
                    <div
                      className="progress-bar-fill"
                      style={{ width: `${processStatus.progress}%` }}
                    />
                  </div>
                  {processStatus.total_rows > 0 && (
                    <p className="text-xs text-gray-500 mt-2">
                      {processStatus.processed_rows} / {processStatus.total_rows} righe elaborate
                    </p>
                  )}
                </div>
              )}

              {processStatus.status === 'error' && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700">{processStatus.message}</p>
                </div>
              )}

              {processStatus.status === 'completed' && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <p className="text-sm text-green-700">{processStatus.message}</p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={startProcessing}
                  disabled={processStatus.status === 'running' || inputFiles.length === 0}
                  className="flex-1 btn-primary py-3 rounded-sm font-medium flex items-center justify-center gap-2"
                  data-testid="process-button"
                >
                  {processStatus.status === 'running' ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Elaborazione...
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5" />
                      Processa
                    </>
                  )}
                </button>
                
                {(processStatus.status === 'completed' || processStatus.status === 'error') && (
                  <button
                    onClick={resetProcess}
                    className="px-4 py-3 border border-gray-300 rounded-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    data-testid="reset-button"
                  >
                    <RefreshCw className="w-5 h-5" />
                  </button>
                )}
              </div>

              {inputFiles.length === 0 && processStatus.status === 'idle' && (
                <p className="text-sm text-gray-500 mt-3 text-center">
                  Carica un file Excel per iniziare
                </p>
              )}
            </div>
          </div>

          {/* Send Outreach Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Invio Outreach
              </h2>
              <span className={`status-badge ${
                sendStatus.status === 'running'   ? 'status-running' :
                sendStatus.status === 'completed' ? 'status-completed' :
                sendStatus.status === 'stopped'   ? 'status-idle' :
                sendStatus.status === 'error'     ? 'status-error' : 'status-idle'
              }`} data-testid="send-status-badge">
                {sendStatus.status === 'running'   ? 'In corso' :
                 sendStatus.status === 'completed' ? 'Completato' :
                 sendStatus.status === 'stopped'   ? 'Interrotto' :
                 sendStatus.status === 'error'     ? 'Errore' : 'In attesa'}
              </span>
            </div>

            {/* Progress Bar */}
            {(sendStatus.status === 'running' || sendStatus.status === 'completed' || sendStatus.status === 'stopped') && (
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>{sendStatus.message}</span>
                  <span data-testid="send-progress-percentage">{sendStatus.progress}%</span>
                </div>
                <div className="progress-bar-container" data-testid="send-progress-bar">
                  <div
                    className={`progress-bar-fill ${sendStatus.status === 'stopped' ? '!bg-orange-400' : ''}`}
                    style={{ width: `${sendStatus.progress}%` }}
                  />
                </div>
                <div className="flex gap-4 text-xs text-gray-500 mt-2">
                  <span className="flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {sendStatus.sent_emails} email
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageCircle className="w-3 h-3" /> {sendStatus.sent_whatsapp} WhatsApp
                  </span>
                  {sendStatus.sent_sms > 0 && (
                    <span className="flex items-center gap-1">
                      <MessageCircle className="w-3 h-3" /> {sendStatus.sent_sms} SMS
                    </span>
                  )}
                  {sendStatus.errors > 0 && (
                    <span className="text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {sendStatus.errors} errori
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Stop Button */}
            {sendStatus.status === 'running' && (
              <button
                onClick={stopSending}
                className="w-full mb-4 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-sm font-medium flex items-center justify-center gap-2 transition-colors"
                data-testid="stop-send-button"
              >
                <StopCircle className="w-4 h-4" />
                Ferma Invio
              </button>
            )}

            {sendStatus.status === 'error' && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700">{sendStatus.message}</p>
              </div>
            )}

            {sendStatus.status === 'completed' && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="font-medium text-green-700">Invio completato!</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-white p-2 rounded border border-green-100">
                    <div className="text-green-600 font-medium text-lg">{sendStatus.sent_emails}</div>
                    <div className="text-gray-500 text-xs">Email inviate</div>
                  </div>
                  <div className="bg-white p-2 rounded border border-green-100">
                    <div className="text-green-600 font-medium text-lg">{sendStatus.sent_whatsapp}</div>
                    <div className="text-gray-500 text-xs">WhatsApp inviati</div>
                  </div>
                  <div className="bg-white p-2 rounded border border-green-100">
                    <div className="text-blue-600 font-medium text-lg">{sendStatus.sent_sms || 0}</div>
                    <div className="text-gray-500 text-xs">SMS inviati</div>
                  </div>
                  <div className="bg-white p-2 rounded border border-green-100">
                    <div className={`font-medium text-lg ${sendStatus.errors > 0 ? 'text-red-500' : 'text-gray-600'}`}>{sendStatus.errors}</div>
                    <div className="text-gray-500 text-xs">Errori</div>
                  </div>
                </div>
              </div>
            )}

            {sendStatus.status === 'stopped' && (
              <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-md">
                <div className="flex items-center gap-2 mb-3">
                  <StopCircle className="w-5 h-5 text-orange-500" />
                  <span className="font-medium text-orange-700">Invio interrotto</span>
                </div>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="bg-white p-2 rounded border border-orange-100">
                    <div className="text-orange-600 font-medium text-lg">{sendStatus.sent_emails}</div>
                    <div className="text-gray-500 text-xs">Email</div>
                  </div>
                  <div className="bg-white p-2 rounded border border-orange-100">
                    <div className="text-orange-600 font-medium text-lg">{sendStatus.sent_whatsapp}</div>
                    <div className="text-gray-500 text-xs">WhatsApp</div>
                  </div>
                  <div className="bg-white p-2 rounded border border-orange-100">
                    <div className="text-orange-600 font-medium text-lg">{sendStatus.sent_sms || 0}</div>
                    <div className="text-gray-500 text-xs">SMS</div>
                  </div>
                </div>
                <p className="text-xs text-orange-600 mt-2">Riavviando l'invio, i contatti già inviati verranno saltati.</p>
              </div>
            )}

            {(sendStatus.status === 'completed' || sendStatus.status === 'error' || sendStatus.status === 'stopped') && (
              <button
                onClick={resetSendStatus}
                className="w-full px-4 py-2 border border-gray-300 rounded-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                data-testid="reset-send-button"
              >
                <RefreshCw className="w-4 h-4" />
                Reset
              </button>
            )}

            {sendStatus.status === 'idle' && (
              <p className="text-sm text-gray-500 text-center">
                Seleziona un file dalla tab Output e clicca <Send className="w-4 h-4 inline" /> per inviare
              </p>
            )}
          </div>

          {/* File Manager Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border border-gray-200">
              {/* Tabs */}
              <div className="border-b border-gray-200">
                <div className="flex">
                  <button
                    onClick={() => setActiveTab('input')}
                    className={`flex items-center gap-2 px-6 py-4 font-medium text-sm transition-colors ${
                      activeTab === 'input'
                        ? 'text-[#002DB3] border-b-2 border-[#002DB3]'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid="tab-input"
                  >
                    <FolderInput className="w-5 h-5" />
                    Input ({inputFiles.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('output')}
                    className={`flex items-center gap-2 px-6 py-4 font-medium text-sm transition-colors ${
                      activeTab === 'output'
                        ? 'text-[#002DB3] border-b-2 border-[#002DB3]'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid="tab-output"
                  >
                    <FolderOutput className="w-5 h-5" />
                    Output ({outputFiles.length})
                  </button>
                </div>
              </div>

              {/* File List */}
              <div className="p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-gray-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                    {activeTab === 'input' ? 'File di Input' : 'File di Output'}
                  </h3>
                  <div className="flex items-center gap-2">
                    {activeTab === 'output' && (
                      <>
                        <input
                          type="file"
                          ref={editedFileInputRef}
                          onChange={(e) => { handleEditedUpload(e.target.files[0]); e.target.value = ''; }}
                          accept=".csv"
                          className="hidden"
                          data-testid="edited-file-input"
                        />
                        <button
                          onClick={() => editedFileInputRef.current?.click()}
                          disabled={uploading}
                          className="flex items-center gap-2 px-3 py-1.5 bg-[#002DB3] text-white text-sm rounded-sm hover:bg-[#0024A0] transition-colors disabled:opacity-50"
                          title="Carica un CSV editato (es. file _check verificato) e invialo"
                          data-testid="upload-edited-button"
                        >
                          <Upload className="w-4 h-4" />
                          Carica CSV editato
                        </button>
                      </>
                    )}
                    <button
                      onClick={fetchFiles}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                      data-testid="refresh-files-button"
                    >
                      <RefreshCw className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {files.length === 0 ? (
                  <div className="text-center py-12" data-testid="empty-files">
                    <FileSpreadsheet className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">
                      {activeTab === 'input' 
                        ? 'Nessun file di input. Carica un file Excel per iniziare.'
                        : 'Nessun file di output. Avvia un\'elaborazione per generare i risultati.'}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="files-table">
                      <thead>
                        <tr className="text-left text-sm text-gray-500 border-b border-gray-200">
                          <th className="pb-3 font-medium">Nome File</th>
                          <th className="pb-3 font-medium">Dimensione</th>
                          <th className="pb-3 font-medium">Modificato</th>
                          <th className="pb-3 font-medium text-right">Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {files.map((file) => (
                          <tr key={file.name} className="file-row border-b border-gray-100 last:border-0">
                            <td className="py-3">
                              <div className="flex items-center gap-3">
                                <File className="w-5 h-5 text-gray-400" />
                                <span className="font-medium text-gray-900" data-testid={`file-name-${file.name}`}>
                                  {file.name}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 text-sm text-gray-600">
                              {formatFileSize(file.size)}
                            </td>
                            <td className="py-3 text-sm text-gray-600">
                              {formatDate(file.modified)}
                            </td>
                            <td className="py-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                {activeTab === 'output' && file.name.includes('_safe') && (
                                  <button
                                    onClick={() => openSendModal(file.name)}
                                    className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                                    title="Invia Email/WhatsApp"
                                    data-testid={`send-${file.name}`}
                                  >
                                    <Send className="w-5 h-5" />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDownload(activeTab, file.name)}
                                  className="p-2 text-gray-500 hover:text-[#002DB3] hover:bg-blue-50 rounded transition-colors"
                                  title="Scarica"
                                  data-testid={`download-${file.name}`}
                                >
                                  <Download className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => handleDelete(activeTab, file.name)}
                                  className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Elimina"
                                  data-testid={`delete-${file.name}`}
                                >
                                  <Trash2 className="w-5 h-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Send Modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="send-modal">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Invia Outreach
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              File: <span className="font-medium">{selectedFileForSend}</span>
            </p>
            
            <div className="space-y-3 mb-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendOptions.send_email}
                  onChange={(e) => setSendOptions({...sendOptions, send_email: e.target.checked})}
                  className="w-4 h-4 text-[#002DB3] border-gray-300 rounded focus:ring-[#002DB3]"
                />
                <Mail className="w-5 h-5 text-gray-500" />
                <span className="text-gray-700">Invia Email (Brevo)</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendOptions.send_whatsapp}
                  onChange={(e) => setSendOptions({...sendOptions, send_whatsapp: e.target.checked})}
                  className="w-4 h-4 text-[#002DB3] border-gray-300 rounded focus:ring-[#002DB3]"
                />
                <MessageCircle className="w-5 h-5 text-gray-500" />
                <span className="text-gray-700">Invia WhatsApp + SMS fallback (Twilio/Brevo)</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer mt-4 pt-4 border-t border-gray-200">
                <input
                  type="checkbox"
                  checked={sendOptions.dry_run}
                  onChange={(e) => setSendOptions({...sendOptions, dry_run: e.target.checked})}
                  className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                />
                <span className="text-gray-700">🧪 Test Mode (non invia realmente)</span>
              </label>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowSendModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={startSending}
                disabled={!sendOptions.send_email && !sendOptions.send_whatsapp}
                className="flex-1 btn-primary py-2 rounded-sm font-medium flex items-center justify-center gap-2"
                data-testid="confirm-send-button"
              >
                <Send className="w-4 h-4" />
                {sendOptions.dry_run ? 'Test Invio' : 'Invia'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Status Overlay - mini notification */}
      {sendStatus.status === 'running' && (
        <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 p-3 z-40" data-testid="send-status-mini">
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 text-[#002DB3] animate-spin" />
            <span className="text-sm font-medium text-gray-900">Invio: {sendStatus.progress}%</span>
          </div>
        </div>
      )}
    </div>
  );
}
